import { createApi } from '@reduxjs/toolkit/query/react'
import { API, Auth, graphqlOperation } from 'aws-amplify'
import { prettyError } from '@grin-rnd/grin-api-sdk/dist/utils/ErrorUtils'
import { fetchAll, GraphQLMutationTypes, mutateEntity } from 'utils/graphqlUtils'
import { logAuthError, logError } from 'utils/logUtils'
import { ModuleTags } from './modules/consts'

/**
 * Minimum gap between two forced refreshes. fetchGrinNotifications polls every 30s while the
 * bell is open, so a genuinely dead session produces a burst of 401s. Without a gate each one is its own
 * Cognito InitiateAuth, and TooManyRequestsException has already been seen in production
 * on a related flow.
 *
 * Note this throttles the *refresh*, never the retry — see refreshSession.
 */
const REFRESH_COOLDOWN_MS = 30 * 1000

/**
 * Neither Auth.currentAuthenticatedUser() nor user.refreshSession() has a timeout or an
 * abort. Without this bound, a refresh that never settles (wedged socket, captive portal,
 * Cognito hang) would leave inflightRefresh permanently non-null, and every subsequent 401
 * anywhere in the app would join that promise and await it forever — grinBaseQuery would
 * never return, so RTK Query would never settle and isLoading would stay true.
 */
const REFRESH_TIMEOUT_MS = 10 * 1000

let inflightRefresh = null
let lastRefreshAt = 0

const forceRefresh = async () => {
  const user = await Auth.currentAuthenticatedUser()
  // currentAuthenticatedUser() already resolves the session (via currentUserPoolUser), so
  // read the refresh token off the user rather than making a second round-trip.
  const refreshToken = user.getSignInUserSession()?.getRefreshToken()

  if (!refreshToken) {
    throw new Error('no refresh token available')
  }

  return new Promise((resolve, reject) =>
    user.refreshSession(refreshToken, (error, refreshed) => {
      if (error) {
        logAuthError('refreshSession', error)
        reject(error)
      } else {
        resolve(refreshed)
      }
    })
  )
}

/**
 * Ensures the client has a freshly refreshed session, and resolves when the caller may
 * retry. Rejects only when there is no reason to believe a retry would fare any better.
 *
 * `Auth.currentSession()` refreshes only when it considers the token expired, which is
 * exactly what we can't trust here — we have a 401 with a token the client believes is
 * valid. `refreshSession` is the explicit path, and it is callback-based in Amplify v4.
 *
 * The cooldown deliberately **resolves** rather than rejecting. A 401 arriving moments
 * after a successful refresh is the common case, not an edge one: the notification poll
 * and a mark-as-read click both go out with the same stale token, so the poll's 401
 * triggers the refresh and the click's 401 lands just behind it. Refusing to retry there
 * reproduces the reported symptom exactly — the row reappears and the count bounces —
 * while a fresh token is already sitting in storage and the retry costs no Cognito call.
 * Throttle the refresh, never the retry.
 */
const refreshSession = async () => {
  // The timeout bounds each *caller's* wait, not the refresh itself. If it bounded the
  // shared promise, a caller timing out would clear inflightRefresh while forceRefresh was
  // still running, and the next 401 would start a second concurrent InitiateAuth — and a
  // late success would never stamp lastRefreshAt. So inflightRefresh tracks the underlying
  // refresh and is cleared only when that settles.
  if (!inflightRefresh) {
    if (Date.now() - lastRefreshAt < REFRESH_COOLDOWN_MS) {
      return null
    }

    inflightRefresh = forceRefresh()
    inflightRefresh
      .then(() => {
        // Stamped only on success. Arming the cooldown after a network blip would suppress
        // every refresh for the next 30s, exactly when recovery matters most.
        lastRefreshAt = Date.now()
      })
      // Swallowed so a rejection nobody is still awaiting (every caller having timed out)
      // does not surface as an unhandled rejection; callers see it through the race below.
      .catch(() => {})
      .finally(() => {
        inflightRefresh = null
      })
  }

  let timer
  try {
    return await Promise.race([
      inflightRefresh,
      new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(new Error('session refresh timed out')), REFRESH_TIMEOUT_MS)
      })
    ])
  } finally {
    clearTimeout(timer)
  }
}

export const REQUEST_TYPE = {
  GRAPHQL: 'graphql',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
  GET: 'GET',
  GRAPHQL_FETCH_ALL: 'GRAPHQL_FETCH_ALL',
  GRAPHQL_MUTATION: 'GRAPHQL_MUTATION'
}

/**
 * Request types that go to AppSync under the Cognito user-pool authorizer.
 *
 * The retry is deliberately limited to these. A REST 401 through API Gateway is very often
 * an *application* 401 that a Cognito refresh cannot fix and a replay should not repeat:
 * grin-pms-service answers 401 GREYFINCH_UNAUTHORIZED when the practice's *Greyfinch*
 * token is stale (and fetchGreyfinchSummary refetches on every patient open), and
 * grin-accounts-service returns 401 from patient hard-delete *after* the cascade has
 * partially run. AppSync, by contrast, evaluates user-pool auth before the resolver, so a
 * 401 there means the request never executed and replaying it is safe.
 */
const APPSYNC_REQUEST_TYPES = [REQUEST_TYPE.GRAPHQL, REQUEST_TYPE.GRAPHQL_FETCH_ALL, REQUEST_TYPE.GRAPHQL_MUTATION]

/**
 * Detects the transport 401 AppSync returns when a request arrived without a usable token,
 * as distinct from anything a resolver raised.
 *
 * Amplify's REST client rethrows the raw axios error, so the status lives at
 * `error.response.status`; the GraphQL client turns a failed POST into
 * `{ data: {}, errors: [new GraphQLError(axiosMessage, null, null, null, null, err)] }`
 * (GraphQLAPI.js) — note `path` is explicitly null and there is no `errorType`.
 *
 * That is the distinction that makes replaying a *mutation* safe. An error the server
 * produced always carries a `path` (the field it came from), and AppSync adds `errorType`;
 * such an error may have been raised after the resolver did work, so replaying it could
 * duplicate a side effect. A pre-resolver transport failure has neither, which means the
 * request never reached a resolver at all.
 *
 * Also deliberately not matching a bare `Unauthorized`: an Amplify @auth denial arrives as
 * HTTP 200 with `errorType: 'Unauthorized'`, and several Grin services return a literal
 * `{ message: 'Unauthorized' }` role check. Neither is fixed by refreshing.
 */
const isUnauthorizedError = error => {
  if (!error) {
    return false
  }

  if (error.response?.status === 401) {
    return true
  }

  const graphqlErrors = error.errors || []

  // Any server-attributed error means we cannot prove the resolver didn't run.
  if (graphqlErrors.some(e => e?.path || e?.errorType)) {
    return false
  }

  const messages = graphqlErrors.map(e => e?.message || '').join(' ')
  return /status code 401/i.test(`${messages} ${error.message || ''}`)
}

const shouldRetryUnauthorized = (args, error) =>
  APPSYNC_REQUEST_TYPES.includes(args?.type ?? REQUEST_TYPE.GRAPHQL) && isUnauthorizedError(error)

/**
 * Executes an API request based on the given request type (GraphQL, REST methods, or Fetch All).
 *
 * @async
 * @function runRequest
 * @param {Object} args - The arguments for the API request.
 * @param {Object} [args.data={}] - The request payload data (used for REST API requests).
 * @param {Object} [args.variables={}] - The variables for GraphQL queries.
 * @param {string} [args.queryName=''] - The query name, used only for GraphQL Fetch All requests.
 * @param {string} [args.apiName='grinServerlessApi'] - The API name (used for REST API requests).
 * @param {string} [args.path='/'] - The API endpoint path.
 * @param {string} [args.type=REQUEST_TYPE.GRAPHQL] - The type of request (GraphQL, GET, POST, PUT, PATCH, DELETE, GRAPHQL_FETCH_ALL, GRAPHQL_MUTATION). @See REQUEST_TYPE
 * @param {Object} [args.query={}] - The GraphQL query operation or fetch request.
 * @param {string} [args.entityType=GraphQLMutationTypes.Update] - The mutation type used for GraphQL mutations (Update/Delete). @see GraphQLMutationTypes
 * @param {string} [args.mutationType=] - The entity type used for GraphQL mutations.
 * @param {string} [args.fields] - The fields to be returned or updated in a GraphQL mutation.
 * @param {string} [args.id] - The entity ID used in GraphQL mutations.
 *
 * @returns {Promise<Object>} - Resolves with `{ data: result }` if successful, otherwise returns `{ error }`.
 *
 * @throws {Error} Returns an error object if the API request fails.
 */
const runRequest = async args => {
  const {
    data = {},
    variables = {},
    apiName = 'grinServerlessApi',
    path = '/',
    type = REQUEST_TYPE.GRAPHQL,
    query = {},
    queryName = '',
    entityType = '',
    mutationType = GraphQLMutationTypes.Update,
    fields,
    queryParams,
    id
  } = args

  try {
    let result
    switch (type) {
      case REQUEST_TYPE.GRAPHQL:
        result = await API.graphql(graphqlOperation(query, variables))
        break
      case REQUEST_TYPE.POST:
        result = await API.post(apiName, path, { body: data })
        break
      case REQUEST_TYPE.PUT:
        result = await API.put(apiName, path, { body: data })
        break
      case REQUEST_TYPE.PATCH:
        result = await API.patch(apiName, path, { body: data })
        break
      case REQUEST_TYPE.DELETE:
        result = await API.del(apiName, path)
        break
      case REQUEST_TYPE.GRAPHQL_FETCH_ALL:
        result = await fetchAll(query, variables, queryName)
        break
      case REQUEST_TYPE.GRAPHQL_MUTATION:
        result = await mutateEntity({ id, input: variables, mutationType, entityType, fields })
        break
      case REQUEST_TYPE.GET:
      default:
        result = await API.get(apiName, path, { queryStringParameters: queryParams })
        break
    }

    return result.data ? result : { data: result }
  } catch (error) {
    return { error }
  }
}

/**
 * Wraps {@link runRequest} with a single retry for AppSync 401s.
 *
 * A 401 from AppSync means the request reached it without a usable token — not that the
 * user lacks permission. Without a retry the caller just fails, and any optimistic UI
 * update is rolled back, which reads to the user as "my click did nothing" (e.g. HI
 * users' notifications reappearing after mark-as-read). Forcing a session refresh and
 * replaying the request once recovers transparently.
 *
 * Scope note: ~196 call sites dispatch API.graphql directly from epics, utils and
 * components and never pass through here. This protects the RTK Query endpoints only.
 */
export const grinBaseQuery = async args => {
  const result = await runRequest(args)

  if (!result.error || !shouldRetryUnauthorized(args, result.error)) {
    return result
  }

  logError('grinBaseQuery: request was unauthorized, refreshing the session and retrying once', {
    type: args?.type,
    path: args?.path,
    entityType: args?.entityType,
    error: prettyError(result.error)
  })

  try {
    await refreshSession()
  } catch (error) {
    logError('grinBaseQuery: could not refresh the session after a 401', {
      type: args?.type,
      path: args?.path,
      entityType: args?.entityType,
      error: prettyError(error)
    })
    return result
  }

  const retried = await runRequest(args)

  if (retried.error) {
    logError('grinBaseQuery: request still failed after refreshing the session', {
      type: args?.type,
      path: args?.path,
      entityType: args?.entityType,
      error: prettyError(retried.error)
    })
  }

  return retried
}

export const api = createApi({
  baseQuery: grinBaseQuery,
  tagTypes: Object.values(ModuleTags),
  endpoints: () => ({})
})
